import { useCallback, useLayoutEffect, useRef } from "react";

const SCROLL_BOTTOM_THRESHOLD = 80;

interface ScrollSnapshot {
  replyCount: number;
  scrollHeight: number;
  scrollTop: number;
}

interface UseChatScrollBehaviorOptions {
  replyCount: number;
}

export function useChatScrollBehavior({ replyCount }: UseChatScrollBehaviorOptions): {
  captureAnchorBeforePrepend: () => boolean;
  onScroll: () => void;
  requestScrollToBottomAfterNextReply: () => void;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  scrollToBottom: () => void;
} {
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasInitialScrolledRef = useRef(false);
  const prependSnapshotRef = useRef<ScrollSnapshot | null>(null);
  const scrollToBottomAfterReplyCountRef = useRef<number | null>(null);
  const wasNearBottomRef = useRef(true);

  const updateWasNearBottom = useCallback(() => {
    const scrollElement = scrollRef.current;

    if (!scrollElement) {
      return;
    }

    wasNearBottomRef.current =
      scrollElement.scrollHeight - scrollElement.scrollTop - scrollElement.clientHeight <= SCROLL_BOTTOM_THRESHOLD;
  }, []);

  const scrollToBottom = useCallback(() => {
    const scrollElement = scrollRef.current;

    if (!scrollElement) {
      return;
    }

    scrollElement.scrollTo({ top: scrollElement.scrollHeight });
    updateWasNearBottom();
  }, [updateWasNearBottom]);

  const captureAnchorBeforePrepend = useCallback(() => {
    if (prependSnapshotRef.current) {
      return false;
    }

    const scrollElement = scrollRef.current;

    if (!scrollElement) {
      return true;
    }

    prependSnapshotRef.current = {
      replyCount,
      scrollHeight: scrollElement.scrollHeight,
      scrollTop: scrollElement.scrollTop,
    };

    return true;
  }, [replyCount]);

  const requestScrollToBottomAfterNextReply = useCallback(() => {
    scrollToBottomAfterReplyCountRef.current = replyCount;
  }, [replyCount]);

  useLayoutEffect(() => {
    const scrollElement = scrollRef.current;

    if (!scrollElement) {
      return;
    }

    if (replyCount && !hasInitialScrolledRef.current) {
      scrollToBottom();
      hasInitialScrolledRef.current = true;

      return;
    }

    const prependSnapshot = prependSnapshotRef.current;
    if (prependSnapshot) {
      if (replyCount <= prependSnapshot.replyCount) {
        return;
      }

      const heightDelta = scrollElement.scrollHeight - prependSnapshot.scrollHeight;

      scrollElement.scrollTop = prependSnapshot.scrollTop + heightDelta;
      updateWasNearBottom();
      prependSnapshotRef.current = null;

      return;
    }

    const scrollAfterReplyCount = scrollToBottomAfterReplyCountRef.current;
    if (scrollAfterReplyCount !== null) {
      if (replyCount <= scrollAfterReplyCount) {
        return;
      }

      scrollToBottom();
      scrollToBottomAfterReplyCountRef.current = null;

      return;
    }

    if (wasNearBottomRef.current) {
      scrollToBottom();
    }
  }, [replyCount, scrollToBottom, updateWasNearBottom]);

  return {
    captureAnchorBeforePrepend,
    onScroll: updateWasNearBottom,
    requestScrollToBottomAfterNextReply,
    scrollRef,
    scrollToBottom,
  };
}
